import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Modal,
  Progress,
  Space,
  Typography,
  Row,
  Col,
  Form,
  DatePicker,
  Switch,
  Tooltip,
  Input,
  Avatar,
  Spin,
} from "antd";
import {
  MinusCircleOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useInfoUser } from "../../../store/UserStore";
import "./VotedModal.scss";
import apiFactory from "../../../api";
import {
  getColorFromInitial,
  openNotificationError,
  openNotificationSuccess,
} from "../../../utils/Utils";
import { useImageLoader } from "../../customHook/ImageLoader";
import { last } from "lodash";
import { v4 as uuidv4 } from "uuid"; // ⭐ THÊM IMPORT NÀY

const { Title, Text } = Typography;

const VoterAvatarSmall = ({ voter }) => {
  const {
    imageUrl,
    loading: isImageLoading,
    error: imageError,
  } = useImageLoader(voter?.avatar);
  const briefName = last(voter?.name?.trim()?.split(" "))?.[0] || "U";
  return (
    <Tooltip title={voter?.name || voter?.username}>
      {isImageLoading ? (
        <Avatar className="voter-avatar">
          <Spin size="small" />
        </Avatar>
      ) : !imageError ? (
        <Avatar
          className="voter-avatar"
          src={imageUrl}
          style={{ backgroundColor: getColorFromInitial(briefName) }}
        >
          {voter?.name?.[0]?.toUpperCase()}
        </Avatar>
      ) : (
        <Avatar
          className="voter-avatar"
          style={{ backgroundColor: getColorFromInitial(briefName) }}
        >
          {voter?.name?.[0]?.toUpperCase()}
        </Avatar>
      )}
    </Tooltip>
  );
};

const VoterAvatarList = ({ voters = [], totalCount = 0 }) => {
  if (totalCount === 0) return <span>0</span>;

  const hiddenCount = totalCount - voters?.length;

  return (
    <div className="voter-avatar-list-container">
      {voters?.map((voter, index) => (
        <VoterAvatarSmall key={voter?.userId || index} voter={voter} />
      ))}
      {hiddenCount > 0 && (
        <Tooltip title={`${hiddenCount} Other voters`}>
          <Avatar className="voter-avatar more-avatar">+{hiddenCount}</Avatar>
        </Tooltip>
      )}
    </div>
  );
};

const PollOption = ({
  option,
  totalVotes,
  isSelected,
  onSelect,
  isExpiredDeadline,
  showResults,
  hideVoters,
  voterDetailsCache,
  selectedOptionsCount,
}) => {
  const colors = {
    selected: { stroke: "#93c5fd", trail: "#eff6ff" },
    default: { stroke: "#d1d5db", trail: "#f3f4f6" },
  };
  const currentColors = isSelected ? colors.selected : colors.default;

  const percentage = totalVotes > 0 ? (option.voteCount / totalVotes) * 100 : 0;
  const voteCount = option?.voteCount;
  const realVoteCountFromServer = option?.userIds?.length;

  let displayPercentage = 0;

  if (showResults) {
    displayPercentage = totalVotes > 0 ? (voteCount / totalVotes) * 100 : 0;
  } else if (isSelected) {
    displayPercentage = 100 / selectedOptionsCount;
  }

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    const isUpperCase = text === text?.toUpperCase();
    if (isUpperCase || (!text?.includes(" ") && text?.length > maxLength)) {
      maxLength = maxLength - 5;
    }
    if (text?.length <= maxLength) {
      return text;
    }
    let truncated = text?.substring(0, maxLength);
    const lastSpace = truncated?.lastIndexOf(" ");
    if (lastSpace > 0) {
      truncated = truncated?.substring(0, lastSpace);
    }
    return truncated + " ...";
  };

  const voterList = useMemo(() => {
    const firstTenUserIds = option?.userIds?.slice(0, 3) || [];
    return firstTenUserIds
      ?.map((id) => voterDetailsCache?.[id])
      ?.filter(Boolean);
  }, [option.userIds, voterDetailsCache]);

  return (
    <div
      className="poll-option-wrapper-v2"
      onClick={!isExpiredDeadline ? onSelect : undefined}
    >
      <div className="flex items-center justify-center w-full">
        <Checkbox
          checked={isSelected}
          className="mr-2"
          style={{ zIndex: 2 }}
          disabled={isExpiredDeadline}
        />
        <Progress
          percent={showResults ? percentage : 0}
          strokeColor={currentColors.stroke}
          trailColor={currentColors.trail}
          showInfo={false}
          className="w-full"
        />
      </div>
      <div className="poll-option-content-v2">
        {option?.optionText?.length > 40 ? (
          <Tooltip title={option?.optionText} align={"center"}>
            <span className="option-text-v2">
              {truncateText(option?.optionText, 40)}
            </span>
          </Tooltip>
        ) : (
          <span className="option-text-v2">{option?.optionText}</span>
        )}
        <span className="vote-count-v2">
          {showResults ? (
            hideVoters ? (
              <span>{voteCount}</span>
            ) : (
              <VoterAvatarList
                voters={voterList}
                totalCount={realVoteCountFromServer}
              />
            )
          ) : (
            <Tooltip title="Kết quả sẽ hiển thị sau khi bạn bình chọn">
              <span>-</span>
            </Tooltip>
          )}
        </span>
      </div>
    </div>
  );
};

const VotedModal = ({
  voteMessageData,
  onCancel,
  open,
  voterDetailsCache,
  isOwner,
}) => {
  const { user, languageMap } = useInfoUser();

  const [isExpanded, setIsExpanded] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState(new Set());
  const [isExpiredDeadline, setIsExpiredDeadline] = useState(false);

  const [form] = Form.useForm();

  const userHasVoted = useMemo(() => {
    return (
      voteMessageData?.voteOptions?.some((option) =>
        option.userIds?.includes(user?.userId)
      ) || false
    );
  }, [voteMessageData, user]);

  const canShowPreviewResults = useMemo(() => {
    if (!voteMessageData?.hideResults) return true;
    return userHasVoted;
  }, [voteMessageData?.hideResults, userHasVoted]);

  const votePreviewData = useMemo(() => {
    const allOptions = voteMessageData?.voteOptions || [];
    if (!allOptions.length)
      return { previewOptions: [], previewTotalVotes: 0, uniqueVoters: 0 };

    const initialUserVotes = new Set();
    const voters = new Set();

    allOptions.forEach((option) => {
      option.userIds.forEach((id) => voters.add(id));
      if (option.userIds.includes(user?.userId)) {
        initialUserVotes.add(option.voteOptionId);
      }
    });

    const previewOptions = allOptions.map((option) => {
      let voteCount = option.userIds.length;
      const wasInitiallyVoted = initialUserVotes.has(option.voteOptionId);
      const isCurrentlySelected = selectedOptions.has(option.voteOptionId);

      if (!wasInitiallyVoted && isCurrentlySelected) voteCount++;
      else if (wasInitiallyVoted && !isCurrentlySelected) voteCount--;
      return { ...option, voteCount };
    });

    const previewTotalVotes = previewOptions.reduce(
      (total, option) => total + option.voteCount,
      0
    );
    const previewVoters = new Set(voters);

    if (selectedOptions.size > 0 && !userHasVoted)
      previewVoters.add(user?.userId);
    else if (selectedOptions.size === 0 && userHasVoted)
      previewVoters.delete(user?.userId);

    return {
      previewOptions,
      previewTotalVotes,
      uniqueVoters: previewVoters.size,
    };
  }, [voteMessageData, selectedOptions, user?.userId, userHasVoted]);

  const { previewOptions, previewTotalVotes, uniqueVoters } = votePreviewData;
  const hiddenOptionsCount =
    previewOptions.length > 5 ? previewOptions.length - 5 : 0;
  const displayedOptions = isExpanded
    ? previewOptions
    : previewOptions.slice(0, 5);

  const handleOptionChange = (optionId) => {
    setSelectedOptions((prev) => {
      const newSelected = new Set(prev);
      if (voteMessageData?.multipleChoice) {
        if (newSelected.has(optionId)) newSelected.delete(optionId);
        else newSelected.add(optionId);
      } else {
        if (newSelected.has(optionId)) return new Set();
        else return new Set([optionId]);
      }
      return newSelected;
    });
  };

  const handleSubmitVote = async () => {
    if (!isOwner && isExpiredDeadline) {
      openNotificationError("Deadline has expired");
      return;
    }

    const newOptionTexts = (form.getFieldValue("options") || []).filter(
      (opt) => opt && opt.trim() !== ""
    );

    const formattedNewOptions = newOptionTexts.map((text) => ({
      voteOptionId: uuidv4(),
      optionText: text,
    }));

    const req = {
      voteOptionIds: Array.from(selectedOptions),
      voteMessageId: voteMessageData?.voteMessageId,
      options: formattedNewOptions,
    };

    try {
      await apiFactory.messageVoteApi.updateVoteMessage(req);
    } catch (error) {
      console.error(error);
    }
    onCancel();
  };

  const handleUpdateSettings = async (values) => {
    const newOptionTexts = (form.getFieldValue("options") || []).filter(
      (opt) => opt && opt.trim() !== ""
    );

    const formattedNewOptions = newOptionTexts.map((text) => ({
      voteOptionId: uuidv4(),
      optionText: text,
    }));

    const rq = {
      ...values,
      deadline: values.deadline ? values.deadline.toISOString() : null,
      voteMessageId: voteMessageData?.voteMessageId,
      voteOptionIds: Array.from(selectedOptions),
      options: formattedNewOptions,
    };

    try {
      const rs = await apiFactory.messageVoteApi.updateVoteMessage(rq);

      if (rs?.status === 200 && rs.data) {
        openNotificationSuccess("Update successful");
      }
    } catch (error) {}
    onCancel();
  };

  const toggleExpand = () => setIsExpanded((prev) => !prev);
  const toggleAdvancedSettings = () => setShowAdvancedSettings((prev) => !prev);
  const disabledTime = (current) => {
    if (current && dayjs(current).isSame(dayjs(), "day")) {
      const now = dayjs();
      const range = (end) => Array.from({ length: end }, (_, i) => i);
      return {
        disabledHours: () => range(now.hour()),
        disabledMinutes: (hour) =>
          hour === now.hour() ? range(now.minute()) : [],
      };
    }
    return {};
  };
  const FormLabelWithTooltip = ({ title, tooltip }) => (
    <div className="flex items-center">
      <span>{title}</span>
      <Tooltip title={tooltip}>
        <QuestionCircleOutlined className="ml-1 text-gray-400" />
      </Tooltip>
    </div>
  );

  useEffect(() => {
    if (open) {
      const initialSelected = new Set();
      voteMessageData?.voteOptions?.forEach((option) => {
        if (option.userIds.includes(user?.userId)) {
          initialSelected.add(option.voteOptionId);
        }
      });
      setSelectedOptions(initialSelected);

      form.setFieldsValue({
        ...voteMessageData,
        deadline: voteMessageData.deadline
          ? dayjs(voteMessageData.deadline)
          : null,
      });
    } else {
      setShowAdvancedSettings(false);
      setIsExpanded(false);
    }
  }, [open, voteMessageData, user, form]);

  useEffect(() => {
    if (!voteMessageData?.deadline) {
      setIsExpiredDeadline(false);
      return;
    }
    const checkExpiration = () => {
      setIsExpiredDeadline(dayjs().isAfter(dayjs(voteMessageData.deadline)));
    };
    checkExpiration();
    const intervalId = setInterval(checkExpiration, 20000);
    return () => clearInterval(intervalId);
  }, [voteMessageData?.deadline]);

  return (
    <Modal
      width={showAdvancedSettings ? 850 : 520}
      title="Poll"
      open={open}
      onCancel={onCancel}
      footer={
        <div className="flex justify-between items-center w-full">
          <div>
            {isOwner && (
              <Button
                type="text"
                icon={
                  <SettingOutlined
                    className={showAdvancedSettings ? "text-[#1677ff]" : ""}
                  />
                }
                onClick={toggleAdvancedSettings}
              />
            )}
          </div>
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              type="primary"
              onClick={
                showAdvancedSettings ? () => form.submit() : handleSubmitVote
              }
            >
              {showAdvancedSettings ? "Save Settings" : "Accept"}
            </Button>
          </Space>
        </div>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleUpdateSettings}>
        <Row gutter={24}>
          <Col span={showAdvancedSettings ? 16 : 24}>
            <div className="poll-in-modal">
              <div className="pr-4">
                <div className="poll-header-v2">
                  <Title level={5} style={{ margin: 0 }}>
                    {voteMessageData?.voteTitle}
                  </Title>
                  {voteMessageData?.deadline && (
                    <Text type="secondary">{`Ending at ${dayjs(
                      voteMessageData.deadline
                    ).format("HH:mm DD/MM/YYYY")}`}</Text>
                  )}
                </div>
                <Text className="poll-voter-info-v2">
                  {uniqueVoters} Voters
                </Text>
                <Space
                  direction="vertical"
                  size={0}
                  style={{ width: "100%", marginTop: "6px" }}
                >
                  {displayedOptions?.map((option) => (
                    <PollOption
                      key={option.voteOptionId}
                      option={option}
                      totalVotes={previewTotalVotes}
                      isSelected={selectedOptions.has(option.voteOptionId)}
                      onSelect={() => handleOptionChange(option.voteOptionId)}
                      isExpiredDeadline={isExpiredDeadline}
                      showResults={isOwner ? true : canShowPreviewResults}
                      hideVoters={isOwner ? false : voteMessageData?.hideVoters}
                      voterDetailsCache={voterDetailsCache}
                      selectedOptionsCount={selectedOptions?.size}
                    />
                  ))}
                  {hiddenOptionsCount > 0 && (
                    <span
                      className="show-more-button-v2"
                      onClick={toggleExpand}
                    >
                      {isExpanded
                        ? "Hide"
                        : `+ ${hiddenOptionsCount} other options`}
                    </span>
                  )}
                </Space>
                {(isOwner || voteMessageData?.allowAddOptions) && (
                  <Form.Item>
                    {/* ⭐ SỬA ĐỔI TÊN Form.List TẠI ĐÂY ⭐ */}
                    <Form.List name="options">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map(({ key, name, ...restField }, index) => (
                            <Form.Item
                              {...restField}
                              key={key}
                              style={{ marginBottom: 10 }}
                              name={[name]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please enter an option!",
                                },
                                {
                                  validator: (_, value) =>
                                    !value || value.trim() === ""
                                      ? Promise.reject(
                                          new Error(
                                            "Options cannot be left blank!"
                                          )
                                        )
                                      : Promise.resolve(),
                                },
                              ]}
                            >
                              <div className="flex items-center gap-2">
                                <Input
                                  placeholder={`${
                                    languageMap?.[
                                      "modal.message.vote.optionPlaceholder"
                                    ] ?? "Option"
                                  } ${index + 1}`}
                                  style={{ flexGrow: 1 }}
                                  className="ml-6"
                                />
                                {fields.length > 0 ? (
                                  <MinusCircleOutlined
                                    onClick={() => remove(name)}
                                    className="text-gray-500 hover:text-red-500 cursor-pointer text-lg"
                                  />
                                ) : null}
                              </div>
                            </Form.Item>
                          ))}
                          <Button
                            type="link"
                            onClick={() => add()}
                            block
                            icon={<PlusOutlined />}
                            className="add-option-button"
                          >
                            {languageMap?.["modal.message.vote.addOptionBtn"] ??
                              "Add an option"}
                          </Button>
                        </>
                      )}
                    </Form.List>
                  </Form.Item>
                )}
              </div>{" "}
            </div>
          </Col>
          {showAdvancedSettings && isOwner && (
            <Col
              span={8}
              className="advanced-settings border-l border-solid border-gray-200 pl-6"
            >
              <Form.Item label="Voting deadline" name="deadline">
                <DatePicker
                  showTime
                  placeholder="No deadline"
                  className="w-full"
                  format="YYYY-MM-DD HH:mm"
                  disabledDate={(current) =>
                    current && current < dayjs().startOf("day")
                  }
                  disabledTime={disabledTime}
                />
              </Form.Item>
              <div className="font-semibold mb-2">Advanced settings</div>
              <div className="font-semibold mt-4 mb-2">Anonymous voting</div>
              <Form.Item
                labelAlign="left"
                labelCol={{ span: 20 }}
                wrapperCol={{ span: 4 }}
                label={
                  <FormLabelWithTooltip
                    title="Hide results until a vote is cast"
                    tooltip="Poll results will only be shown after a user has voted."
                  />
                }
                name="hideResults"
                valuePropName="checked"
                className="mb-2"
              >
                <Switch />
              </Form.Item>
              <Form.Item
                labelAlign="left"
                labelCol={{ span: 20 }}
                wrapperCol={{ span: 4 }}
                label={
                  <FormLabelWithTooltip
                    title="Hide voters"
                    tooltip="Do not display the names of those who have voted"
                  />
                }
                name="hideVoters"
                valuePropName="checked"
                className="mb-2"
              >
                <Switch />
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>
    </Modal>
  );
};

export { VotedModal };
